import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./ooraReports.css";
import { getOoraJobLogsList } from "../../Redux/Actions/ooraReportsActions";
import { formatDateTime, show } from "./ooraJobLogsHelpers";
import OoraSideNav from "../Oora Sidebar/ooraSideNav";
import "../Oora Sidebar/ooraSideNav.css";

const LIMIT = 10;

const OoraJobLogs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ooraJobLogsList, listLoading } = useSelector((state) => state.ooraJobLogs || {});

  const list = ooraJobLogsList?.data?.list || [];
  const total = ooraJobLogsList?.data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const fetchList = useCallback(() => {
    const body = { page, limit: LIMIT };
    if (search) body.search = search;
    dispatch(getOoraJobLogsList(body));
  }, [dispatch, page, search]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  return (
    <div className="oora-layout">
      <OoraSideNav />
      <div className="oora-layout-main">
        <div className="ojl-wrapper">
          <div className="ojl-header">
            <div className="ojl-header-left">
              <div className="ojl-header-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
              </div>
              <div>
                <h1 className="ojl-title">Oora Job Logs</h1>
                <p className="ojl-subtitle">Research run by job: derived data, facts, debate and report</p>
              </div>
            </div>
          </div>

          <div className="ojl-card">
            <div className="ojl-toolbar">
              <div className="ojl-toolbar-left">
                <div className="ojl-search">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by job id, sector or query"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
              <span className="ojl-count">{total} job{total === 1 ? "" : "s"}</span>
            </div>

            <div className="ojl-table-wrap">
              <table className="ojl-table">
                <thead>
                  <tr>
                    <th style={{ width: 60 }}>#</th>
                    <th style={{ width: 140 }}>Job ID</th>
                    <th style={{ width: 110 }}>Sector</th>
                    <th>Query</th>
                    <th style={{ width: 120 }}>Verdict</th>
                    <th style={{ width: 100 }}>Confidence</th>
                    <th style={{ width: 80 }}>Words</th>
                    <th style={{ width: 80 }}>Calls</th>
                    <th style={{ width: 150 }}>Tokens (in / out)</th>
                    <th style={{ width: 170 }}>Saved At</th>
                    <th style={{ width: 80, textAlign: "right" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {listLoading ? (
                    <tr><td colSpan={11} className="ojl-empty">Loading...</td></tr>
                  ) : list.length === 0 ? (
                    <tr><td colSpan={11} className="ojl-empty">No job logs found</td></tr>
                  ) : (
                    list.map((row, i) => (
                      <tr key={row._id}>
                        <td className="ojl-mono">{(page - 1) * LIMIT + i + 1}</td>
                        <td className="ojl-mono">{row.job_id}</td>
                        <td className="ojl-row-title">{row.sector}</td>
                        <td><div className="ojl-clip" title={row.query}>{row.query}</div></td>
                        <td>
                          {row.degraded ? (
                            <span className="ojl-badge ojl-badge--bad">DEGRADED</span>
                          ) : (
                            <span className="ojl-badge ojl-badge--ok">{show(row.verdict)}</span>
                          )}
                        </td>
                        <td className="ojl-mono">{show(row.debate_confidence)}</td>
                        <td>{show(row.word_count)}</td>
                        <td>{show(row.total_calls)}</td>
                        <td className="ojl-mono">{row.total_input_tokens ?? 0} / {row.total_output_tokens ?? 0}</td>
                        <td>{formatDateTime(row.saved_at)}</td>
                        <td style={{ textAlign: "right" }}>
                          <div className="ojl-actions">
                            <button
                              type="button"
                              className="ojl-icon-btn"
                              title="View"
                              onClick={() => navigate(`/oora-job-logs/view/${row.job_id}`)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="ojl-pagination">
              <span className="ojl-page-info">Page {page} of {totalPages}</span>
              <div className="ojl-page-btns">
                <button type="button" className="ojl-btn ojl-btn--ghost" disabled={page <= 1 || listLoading} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </button>
                <button type="button" className="ojl-btn ojl-btn--ghost" disabled={page >= totalPages || listLoading} onClick={() => setPage((p) => p + 1)}>
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OoraJobLogs;